import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { X, RefreshCw, AlertCircle } from 'lucide-react';

const VERSION_KEY = 'ophilia_app_version';
const BUILD_TIME_KEY = 'ophilia_build_time';

const VersionChecker = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);

  const checkForUpdates = useCallback(async () => {
    try {
      const response = await fetch(`/version.json?_=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) return;

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) return;

      const data = await response.json();
      setServerVersion(data);

      const storedVersion = localStorage.getItem(VERSION_KEY);
      const storedBuild = localStorage.getItem(BUILD_TIME_KEY);

      // First visit — store silently
      if (!storedVersion || !storedBuild) {
        localStorage.setItem(VERSION_KEY, data.version);
        localStorage.setItem(BUILD_TIME_KEY, data.buildTime);
        return;
      }

      // Show banner if version or build time changed
      if (data.version !== storedVersion || data.buildTime !== storedBuild) {
        setShowBanner(true);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(checkForUpdates, 1000);
    const interval = setInterval(checkForUpdates, 2 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkForUpdates();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkForUpdates]);

  const handleReload = () => {
    // Save new version so banner won't show again after reload
    if (serverVersion) {
      localStorage.setItem(VERSION_KEY, serverVersion.version);
      localStorage.setItem(BUILD_TIME_KEY, serverVersion.buildTime);
    }

    // Clear caches then hard-navigate
    const clearAndReload = async () => {
      try {
        if ('caches' in window) {
          const names = await window.caches.keys();
          await Promise.all(names.map(n => window.caches.delete(n)));
        }
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
      } catch (e) { console.error(e); }
      // Hard navigate — works on all browsers including iOS Safari
      window.location.replace(window.location.pathname + '?v=' + Date.now());
    };

    clearAndReload();
  };

  if (!showBanner) return null;

  return (
    <>
      <div
        data-testid="version-update-banner"
        className="fixed top-0 left-0 right-0 z-[9999] bg-orange-500 text-white shadow-2xl"
        style={{ animation: 'vb-slide 0.4s ease-out' }}
      >
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 animate-pulse">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-sm">New version available!</p>
                <p className="text-xs text-white/80">Tap Reload to get the latest update</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                data-testid="version-reload-btn"
                onClick={handleReload}
                size="sm"
                className="bg-white text-orange-600 hover:bg-white/90 text-xs font-bold px-4 shadow-lg"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Reload
              </Button>
              <button
                data-testid="version-dismiss-btn"
                onClick={() => setShowBanner(false)}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        body { padding-top: 56px !important; }
        @keyframes vb-slide {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default VersionChecker;
